import { partial } from 'lodash';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import React, { useEffect, useState, useContext } from 'react';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';

import colours from '~/utils/colours';
import Modal from '~/App/Components/Modal';
import Context from '~/App/Modules/Modals/Context';
import { createFontAwesomeIcon } from '~/App/Components/Attributes/util';

const CloseIcon = styled(createFontAwesomeIcon(faTimesCircle))`
  right: 0.5em;
  font-size: 3em;
  cursor: pointer;
  position: absolute;
  margin: auto;
  top: 0;
  bottom: 0;

  @media (max-width: 540px) {
    position: relative;
    margin: 0;
    margin-left: auto;
    top: auto;
    bottom: auto;
    right: auto;
    font-size: 2em;
  }
`;

const Header = styled.div`
  position: relative;
  width: 100%;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;

  font-weight: bold;
  background-color: white;
  color: ${colours.greyMediumDark};
  border-bottom: 1px solid ${colours.greyMedium};
`;

const HeaderInner = styled.h3`
  font-size: 1.5em;

  &&& {
    margin: 0;
    padding: 0;
    color: ${colours.greyDark};
  }
`;

interface FullScreenModalProps {
  id?: string;
  heading: string;
  children: React.ReactNode;
  containerRef?: React.MutableRefObject<HTMLElement | undefined>;
}

function FullscreenModal({
  heading,
  children,
  containerRef,
}: FullScreenModalProps) {
  const [open, setOpen] = useState(true);
  const { goBack } = useContext(Context);

  const modalProps = {
    isOpen: open,
    onRequestClose: partial(setOpen, false),
    hideActions: true,
    panelRef: containerRef,
    styles: {
      overlay: ({ base }) => ({
        ...base,
        overflow: 'hidden',
      }),
      panel: ({ base }) => ({
        ...base,
        height: '100%',
        border: 'none',
        borderRadius: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
      }),
      content: ({ base }) => ({
        ...base,
        height: '100%',
        width: '100%',
        margin: 0,
        padding: 0,
        maxWidth: 'initial',
        borderRadius: 0,
      }),
      actions: ({ base }) => ({
        ...base,
        backgroundColor: colours.greyMedium,
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
      }),
    },
  };

  useEffect(() => {
    if (open) return;

    goBack();
  }, [open]);

  return (
    <Modal {...modalProps}>
      <Header>
        <HeaderInner>{heading}</HeaderInner>
        <CloseIcon onClick={modalProps.onRequestClose} />
      </Header>
      {children}
    </Modal>
  );
}

FullscreenModal.propTypes = {
  children: PropTypes.node.isRequired,
  heading: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  containerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

export default FullscreenModal;
